import {
  FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { DataButton } from 'components/Shared/SharedComponents';
import Icon from 'src/shared/ui/Icon';
import type { QuickFiltersProps, SelectedValueChange } from 'components/Sections/PsychicsSetMediumSizeImagesClone/declarations';
import { CustomerType, GAReplacementValue } from 'constants/enums';
import { Link } from 'src/__generated__/graphqlTypes';
import type { Store } from 'app-redux/types/storeTypes';
import { GA_QUICK_FILTERS_NAME } from 'components/Sections/PsychicsSetMediumSizeImagesClone/lib/constants';

import styles from '../PsychicFilter.module.scss';

const availabilityFilters: Array<string> = ['isChatPsychics', 'isPhonePsychics', 'isAppointmentPsychics', 'isDMPsychics'];

const getGaData = (
  link: Link,
  isAuthenticated: boolean,
): Partial<Record<GAReplacementValue, string>> => {
  const { _meta: gaMeta = {} } = link?.gaProperties || {};

  return {
    ...(gaMeta[GA_QUICK_FILTERS_NAME]
      && { [GAReplacementValue.NAME]: gaMeta[GA_QUICK_FILTERS_NAME] }),
    [GAReplacementValue.USER]: isAuthenticated
      ? CustomerType.EC.toUpperCase()
      : CustomerType.NC.toUpperCase(),
  };
};
const QuickFilters: FC<QuickFiltersProps> = ({
  chat,
  phone,
  clearButton,
  changeFilter,
  updateFilters,
  selectedFiltersLocal,
}) => {
  const isAuthenticated = useSelector((store: Store) => store.server.auth.isAuthenticated);

  const isChatSelectedOutside = selectedFiltersLocal?.SearchOptions?.isChatPsychics === 'yes';
  const isPhoneSelectedOutside = selectedFiltersLocal?.SearchOptions?.isPhonePsychics === 'yes';
  const [isChatSelected, setIsChatSelected] = useState<boolean>(isChatSelectedOutside || false);
  const [isPhoneSelected, setIsPhoneSelected] = useState<boolean>(isPhoneSelectedOutside || false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const isAllButtonSelected = useMemo(
    () => {
      const availabilityFiltersSelected = availabilityFilters
        .filter((filter) => selectedFiltersLocal?.SearchOptions?.[filter]);

      return availabilityFiltersSelected.length === 0;
    },
    [selectedFiltersLocal],
  );

  useEffect(() => {
    setIsChatSelected(isChatSelectedOutside);
  }, [isChatSelectedOutside]);

  useEffect(() => {
    setIsPhoneSelected(isPhoneSelectedOutside);
  }, [isPhoneSelectedOutside]);

  useEffect(() => {
    if (isProcessing) {
      updateFilters();
      setIsProcessing(false);
    }
  }, [selectedFiltersLocal]);

  return (
    <div className={styles.quickFilters}>
      {phone && (
        <DataButton
          link={phone}
          gaData={getGaData(phone, isAuthenticated)}
          className={cn({
            [styles.selected]: isPhoneSelected,
            [styles.button]: true,
            [styles.phone]: true,
          })}
          onClick={() => {
            setIsPhoneSelected(!isPhoneSelected);
            changeFilter({ type: 'isPhonePsychics', value: 'yes' });
            setIsProcessing(true);
          }}
        >
          {phone.image && (
            <Icon
              image={phone.image}
              className={styles.icon}
            />
          )}
          {phone.title}
        </DataButton>
      )}
      {chat && (
        <DataButton
          link={chat}
          gaData={getGaData(chat, isAuthenticated)}
          className={cn({
            [styles.selected]: isChatSelected,
            [styles.button]: true,
            [styles.chat]: true,
          })}
          onClick={() => {
            setIsChatSelected(!isChatSelected);
            changeFilter({ type: 'isChatPsychics', value: 'yes' });
            setIsProcessing(true);
          }}
        >
          {chat.image && (
            <Icon
              image={chat.image}
              className={styles.icon}
            />
          )}
          {chat.title}
        </DataButton>
      )}
      {clearButton && (
        <DataButton
          link={clearButton}
          gaData={getGaData(clearButton, isAuthenticated)}
          disabled={isAllButtonSelected}
          className={cn({
            [styles.selected]: isAllButtonSelected,
            [styles.button]: true,
            [styles.clear]: true,
          })}
          onClick={() => {
            if (isAllButtonSelected) {
              return;
            }

            setIsChatSelected(false);
            setIsPhoneSelected(false);
            const filters: Array<SelectedValueChange> = [];
            availabilityFilters.forEach((filter) => {
              const selectedFilter = selectedFiltersLocal?.SearchOptions?.[filter] as string;

              if (selectedFilter) {
                filters.push({ type: filter, value: selectedFilter });
              }
            });
            changeFilter(filters);
            setIsProcessing(true);
          }}
        >
          {clearButton.title}
        </DataButton>
      )}
    </div>
  );
};

export default QuickFilters;
